import { useState } from "react";
import { Capacitor } from "@capacitor/core";
import { BellRinging, BellSlash, Info, SealCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import usePushState from "@/lib/hooks/usePushState";

/**
 * Parent · /parent/preferences — push notifications control.
 *
 * Renders one of five finite states:
 *
 *   unsupported   — the device has no PushManager (e.g. iOS Safari <16.4
 *                   not installed, Firefox private window, etc.)
 *   denied        — the browser-level permission was denied; we can only
 *                   guide the user to their OS/browser settings.
 *   default+off   — permission not yet asked; single "Enable" CTA.
 *   granted+off   — permission held but no subscription; show "Enable".
 *   granted+on    — fully subscribed; show "Disable" + endpoint fingerprint.
 *
 * `onPrefChange` is optional — if provided, the caller can also toggle
 * the backend-side `new_message_push` preference in sync.
 */
export default function PushToggle({ prefOn = true, onPrefChange, disabled }) {
  const { loading, state, busy, error, enable, disable } = usePushState();
  const [toastBusy, setToastBusy] = useState(false);

  const handleEnable = async () => {
    setToastBusy(true);
    try {
      await enable();
      toast.success("Push notifications enabled on this device");
      if (onPrefChange && !prefOn) {
        try { await onPrefChange(true); } catch { /* non-fatal */ }
      }
    } catch (err) {
      toast.error(err?.message || "Could not enable push");
    } finally {
      setToastBusy(false);
    }
  };

  const handleDisable = async () => {
    setToastBusy(true);
    try {
      await disable();
      toast.success("Push notifications disabled on this device");
    } catch (err) {
      toast.error(err?.message || "Could not disable push");
    } finally {
      setToastBusy(false);
    }
  };

  if (loading) {
    return (
      <div
        data-testid="push-toggle-loading"
        className="rounded-2xl border border-neutral-200 p-4"
      >
        <div className="h-4 w-40 animate-pulse rounded bg-neutral-100" />
        <div className="mt-2 h-3 w-64 animate-pulse rounded bg-neutral-100" />
      </div>
    );
  }

  // Finite state switch for the status + CTA.
  const { supported, permission, subscribed } = state;

  let fsm = "default";
  if (!supported) fsm = "unsupported";
  else if (permission === "denied") fsm = "denied";
  else if (permission === "granted" && subscribed) fsm = "on";
  else fsm = "off";

  // Capacitor's native shell does not expose the browser PushManager API.
  // Until its separate APNs/FCM delivery path is configured, make that
  // limitation explicit rather than suggesting the PWA Home Screen flow.
  const nativePushUnavailable = fsm === "unsupported" && Capacitor.isNativePlatform();
  const nativePushStatusUnavailable = nativePushUnavailable && state.reason === "status_unavailable";

  const pill = {
    unsupported: {
      label: nativePushUnavailable
        ? nativePushStatusUnavailable
          ? "Push setup unavailable"
          : "Not configured in this app build"
        : "Not supported on this device",
      tone: "bg-neutral-100 text-neutral-500",
    },
    denied:      { label: "Blocked in browser settings",  tone: "bg-[#C6851E]/10 text-[#C6851E]" },
    off:         { label: "Off on this device",           tone: "bg-neutral-100 text-neutral-600" },
    on:          { label: "On for this device",           tone: "bg-primary/10 text-primary-hover" },
  }[fsm];

  return (
    <div
      data-testid="push-toggle"
      data-state={fsm}
      className="rounded-2xl border border-neutral-200 p-4"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {fsm === "on" ? (
              <BellRinging size={16} weight="duotone" className="text-primary-hover" />
            ) : fsm === "denied" ? (
              <BellSlash size={16} weight="duotone" className="text-[#C6851E]" />
            ) : (
              <BellRinging size={16} weight="duotone" className="text-neutral-400" />
            )}
            <p className="font-heading text-sm font-semibold text-neutral-900">
              Push notifications on this device
            </p>
            <span
              data-testid="push-toggle-pill"
              className={`ml-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${pill.tone}`}
            >
              {fsm === "on" && <SealCheck size={10} weight="fill" />}
              {pill.label}
            </span>
          </div>

          {fsm === "unsupported" && (
            <p className="mt-1 text-xs leading-relaxed text-neutral-500">
              {nativePushStatusUnavailable ? (
                <>HoneyAcorn could not check native push setup right now. Try again later. Email alerts still work.</>
              ) : nativePushUnavailable ? (
                <>Push notifications have not been configured for this HoneyAcorn app build. Email alerts still work.</>
              ) : (
                <>Your current browser doesn't support push notifications. Email alerts still work. On an iPhone, add the app to your Home Screen first — then this toggle will light up.</>
              )}
            </p>
          )}

          {fsm === "denied" && (
            <p className="mt-1 flex items-start gap-1.5 text-xs leading-relaxed text-neutral-500">
              <Info size={13} weight="duotone" className="mt-0.5 flex-shrink-0 text-[#C6851E]" />
              <span>
                Notifications are blocked for this site. Open your browser's site settings,
                set <em>Notifications</em> to <em>Allow</em>, then come back and tap Enable.
              </span>
            </p>
          )}

          {fsm === "off" && (
            <p className="mt-1 text-xs leading-relaxed text-neutral-500">
              Get a gentle heads-up when a teacher or the center sends you a message.
              You can turn this off any time.
            </p>
          )}

          {fsm === "on" && (
            <p className="mt-1 text-xs leading-relaxed text-neutral-500">
              You'll receive push notifications for new messages on this device.
              Tap a notification to jump straight to the conversation.
            </p>
          )}

          {error && (
            <p
              data-testid="push-toggle-error"
              className="mt-2 text-xs text-[#C6851E]"
            >
              {error}
            </p>
          )}
        </div>

        <div className="flex-shrink-0">
          {fsm === "off" && (
            <button
              type="button"
              data-testid="push-toggle-enable-btn"
              disabled={busy || toastBusy || disabled}
              onClick={handleEnable}
              className="rounded-full bg-primary px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-primary-hover disabled:opacity-60"
            >
              {busy || toastBusy ? "Enabling…" : "Enable"}
            </button>
          )}
          {fsm === "on" && (
            <button
              type="button"
              data-testid="push-toggle-disable-btn"
              disabled={busy || toastBusy || disabled}
              onClick={handleDisable}
              className="rounded-full border border-neutral-300 bg-white px-4 py-2 text-xs font-semibold text-neutral-700 transition hover:bg-neutral-50 disabled:opacity-60"
            >
              {busy || toastBusy ? "Disabling…" : "Disable"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
